import { createTheme, makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import { openDB } from 'idb';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Route, useLocation } from 'react-router';
import Container from '@material-ui/core/Container';
import ApplicationsList from './components/ApplicationsList';
import Account from './components/apps/account/AccountApp';
import AccountProvider, { AccountContext } from './components/apps/account/context/AccountProvider';
import NewVersionPopup from './components/main/NewVersionPopup';
import NavMenu from './components/NavMenu';
import DbContext from './contexts/DbContext';
import GlobalContext, { GlobalContextProvider } from './contexts/GlobalContext';
import LocalizationContext, { dictionary } from './contexts/LocalizationContext';
import i18n, { normalizeLanguage } from './i18n'

import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

import './styles.css';
import BackToTop from './components/main/ScrollTop';
import SnackbarNotification from './components/components/SnackbarNotification/SnackbarNotification';
import { Switch } from 'react-router-dom';

import AppsEnum from './enums/AppsEnum';

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);

// Satelite apps
const NearmissesApp = React.lazy(() =>
    import('./components/apps/nearmisses/NearmissesApp')
);
const CovidAppWithData = React.lazy(() =>
    import('./components/apps/covid/CovidApp')
);
const TasksApp = React.lazy(() =>
    import('./components/apps/tasks/TasksApp')
);
const SystemInterventionApp = React.lazy(() =>
    import('./components/apps/intervention/SystemInterventionApp')
);
const ActivityRegisterApp = React.lazy(() =>
    import('./components/apps/activities/ActivityRegisterApp')
);
const GuestRegistrationsApp = React.lazy(() =>
    import('./components/apps/guests/GuestRegistrationsApp')
);

// My Journey apps
const FeedbacksSubAppWithData = React.lazy(() =>
    import(/* webpackChunkName: "edp.fee" */'./components/apps/edp/feedbacks/FeedbacksSubApp')
);
const ObjectivesSubAppWithData = React.lazy(() =>
    import(/* webpackChunkName: "edp.obj" */'./components/apps/edp/objectives/ObjectivesSubApp')
);
const FollowUpSubAppWithData = React.lazy(() => 
    import(/* webpackChunkName: "edp.obj" */'./components/apps/edp/follow-up/FollowUpSubApp')
);
const CareerSubAppWithData = React.lazy(() =>
    import(/* webpackChunkName: "edp.car" */'./components/apps/edp/career/CareerSubApp')
);
const OverallAssessmentSubAppWithData = React.lazy(() =>
    import(/* webpackChunkName: "edp.obj" */'./components/apps/edp/appreciation/OverallAssessmentSubApp')
);
const MyTeamSubAppWithData = React.lazy(() =>
    import(/* webpackChunkName: "edp.tea" */'./components/apps/edp/team/MyTeamSubApp')
);

// Synergy apps
const ReferenceYearSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/ReferenceYears/ReferenceYearSubApp')
);
const BusinessCategorySubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/BusinessCategories/BusinessCategorySubApp')
);
const RegionSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/Regions/RegionSubApp')
);
const ReportSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/Reports/ReportSubApp')
);
const OfficeSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/Offices/OfficeSubApp')
);
const DepartmentSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/Departments/DepartmentSubApp')
);
const GroupSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/Groups/GroupSubApp')
);
const GroupTypeSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/GroupTypes/GroupTypeSubApp')
);
const ManagerSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/Managers/ManagerSubApp')
);
const ManagerTypeSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/ManagerTypes/ManagerTypeSubApp')
);
const GroupSizeSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/GroupSizes/GroupSizeSubApp')
);
const BaseHrLevelFamilySubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/BaseHrLevelFamilies/BaseHrLevelFamilySubApp')
);
const ObjectiveSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/Objectives/ObjectiveSubApp')
);
const EmployeeSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/Employees/EmployeeSubApp')
);
const ManagerTypeGroupSizeSubApp = React.lazy(() =>
    import(/* webpackChunkName: "synergy" */'./components/apps/synergy/ManagerTypeGroupSizes/ManagerTypeGroupSizeSubApp')
);

//Document rebranding app
const DocumentRebrandingAppWithData = React.lazy(() =>
    import(/* webpackChunkName: "document" */'./components/apps/rebranding/DocumentRebrandingApp')
);
const DocumentMFileModifyAppWithData = React.lazy(() =>
    import(/* webpackChunkName: "document" */'./components/apps/documents-mfile/modify-document/DocumentMFileModifyApp')
);
const DocumentMFileConfigurationAppWithData = React.lazy(() =>
    import(/* webpackChunkName: "document" */'./components/apps/documents-mfile/configuration-document/DocumentMFileConfigurationApp')
);

const HierarchyApp = React.lazy(() =>
    import(/* webpackChunkName: "hierarchy" */'./components/apps/Hierarchy/HierarchyApp')
);

const LazyLoading = () => {
    const g = useContext(GlobalContext);

    React.useEffect(() =>{
          if (!g.loading) {
              g.setLoading(true);
          }

        return () => g.setLoading(false);
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};

const theme = createTheme({
    typography: {
        fontFamily: `"Gellix", "Open Sans", "Roboto", "Helvetica", "Arial", sans-serif`,
        fontSize: 14,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
    },
    palette: {
        primary: { main: '#002952', dark: '#001B36', light: '#4D6986', contrastText: '#FFFFFF' },
        secondary: { main: '#22B7C5', dark: '#01909D', light: '#5BC5CF', contrastText: '#FFFFFF' },
        tertiary: { main: '#2D6E57', dark: '#205D48', light: '#6C9A89', contrastText: '#FFFFFF' },
        error: { main: '#e74c3c' },
        success: { main: '#2ecc71' },
        warning: { main: '#f1c40f' },
    },
    overrides: {
        MuiTableRow: {
            root: {
                '&:last-child td': {
                    borderBottom: 0,
                },
            },
        },
        MuiTooltip: {
            tooltip: {
                fontSize: '0.8em',
            },
        },
    },
});

export const useGlobalStyles = makeStyles((theme) => ({
    linkButton: {
        float: 'right',
        lineHeight: '41px',
        textDecoration: 'underline',
        color: 'blue',
        opacity: '0.8',
        cursor: 'pointer',
    },
    dialogTitle: {
        fontFamily: 'Gellix',
        color: theme.palette.primary.main,
        fontWeight: 400,
        fontSize: '24px',
        flex: '0 0 auto',
        margin: 0,
        padding: '16px 24px',
    },
    defaultButton: {
        minWidth: 'unset',
        padding: '6px 12px',
    },
    blankItem: {
        fontSize: '15px',
        fontStyle: 'italic',
        color: '#aaa',
    },
    sectionDescription: {
        fontSize: '16px',
        textAlign: 'justify',
        lineHeight: '26px',
        display: 'flex',
        margin: '25px 25px 25px 25px',
    },
    themeSquare: {
        backgroundColor: theme.palette.secondary.main,
        margin: 'auto 8px',
        height: '14px',
        width: '14px',
        flexShrink: 0,
    },
    tinyThemeSquare: {
        backgroundColor: theme.palette.secondary.main,
        margin: 'auto 8px',
        height: '8px',
        width: '8px',
        flexShrink: 0,
    },
}));

const useStyles = makeStyles({
    container: {
        marginTop: '5rem',
    },
});

/**
 * Consomme GlobalContext depuis l'intérieur de GlobalContextProvider.
 * Home étant rendu au-dessus du Provider, il ne peut pas lire isMobile lui-même.
 */
const MainContainer = ({ children }) => {
    const classes = useStyles();
    const { isMobile } = useContext(GlobalContext);

    return <Container className={!isMobile ? classes.container : ''}>{children}</Container>;
};

const Home = () => {
    const location = useLocation();
    const [dbContext, setDbContext] = useState({ getTasks: undefined });
    const { apps, getApps, account, getProfile, setPreferencesLanguage } = useContext(AccountContext);
    const [localization, setLocalization] = useState({
        strings: dictionary,
        lang: i18n.language,
        tArray: (key) => i18n.t(key, { returnObjects: true })
    });

    useEffect(() => {
        if (apps.length === 0) {
            getApps(
                () => {},
                () => {}
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        //Couldn't authenticate user because the app is running offline.
        if (!navigator.onLine) {
            console.log("Couldn't authenticate user because the app is running offline.");
            return;
        }

        // Check if already authenticated
        if (account) return;

        // Check if the current app allows anonymous navigation and on which paths
        const pathParts = location.pathname.split('/');
        const app = apps.find((a) => `${a.url}` === pathParts[1]);
        if (app?.allowAnonymous) {
            if (app.anonymousPaths) {
                if (app.anonymousPaths.includes(location.pathname)) {
                    // Current location is include in anonymousPaths so we should not authenticate
                    return;
                }
            } else if (`/${app.url}` === location.pathname) {
                // No anonymous paths are defined so by default we should not authenticate only on the application root
                return;
            }
        }

        // Forces a full sign-out when authenticated requests fail
        getProfile(() => {
            window.location.href = `/api/employee/get-auth-profile?redirectUrl=${encodeURIComponent(window.location.href)}`;
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        openDB('bba-portal', undefined, {
            upgrade(db) {
                db.createObjectStore('tasks', {
                    keyPath: 'id',
                    autoIncrement: true,
                });
            },
        }).then((context) => {
            setDbContext((c) => ({
                ...c,
                getTasks: () => context.transaction(['tasks'], 'readwrite').objectStore('tasks'),
            }));
        });
    }, [setDbContext]);

    const changeLanguage = useCallback(
        (lang) => {
            const normalizeLang = normalizeLanguage(lang);

            i18n.changeLanguage(normalizeLang);

            setLocalization((l) => ({
                ...l,
                lang: normalizeLang,
            }));
            setPreferencesLanguage(
                normalizeLang,
                () => {},
                () => {}
            );
        },
        [setPreferencesLanguage]
    );

    useEffect(() => {
        dayjs.locale(localization.lang);
    }, [localization.lang]);

    return (
        <>
            <MuiThemeProvider theme={theme}>
                <LocalizationContext.Provider value={localization}>
                    <SnackbarNotification />
                    <DbContext.Provider value={dbContext}>
                        <BackToTop />
                        <NavMenu onLanguageChanged={changeLanguage} />
                        <GlobalContextProvider>
                            <NewVersionPopup />
                            <MainContainer>
                                {apps.length > 0 && (
                                    <>
                                        <Switch>
                                            <Route
                                                path='/near-misses'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <NearmissesApp app={apps.find((a) => a.name === 'near-misses')} />
                                                </React.Suspense>}
                                            />
                                            <Route
                                                path='/covid'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <CovidAppWithData app={apps.find((a) => a.name === 'covid')} />
                                                </React.Suspense>}
                                            />
                                            <Route path='/tasks' render={() => <React.Suspense fallback={<LazyLoading />}>
                                                <TasksApp app={apps.find((a) => a.name === 'tasks')} />
                                            </React.Suspense>} />
                                            <Route
                                                path='/remote-interventions'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <SystemInterventionApp app={apps.find((a) => a.name === 'remote-interventions')} />
                                                </React.Suspense>}
                                            />
                                            <Route
                                                path='/hse-activities'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <ActivityRegisterApp app={apps.find((a) => a.name === 'hse-activities')} />
                                                </React.Suspense>}
                                            />
                                            <Route
                                                path='/guest-registrations'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <GuestRegistrationsApp app={apps.find((a) => a.url === 'guest-registrations')} />
                                                </React.Suspense>}
                                            />
                                            <Route
                                                path='/documents-rebranding'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <DocumentRebrandingAppWithData
                                                        app={apps.find((a) => a.name === 'documents-rebranding')}
                                                    />
                                                </React.Suspense>}
                                            />
                                            <Route
                                                path='/edp/feedbacks'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <FeedbacksSubAppWithData
                                                        app={apps.find((a) => a.name === 'edp')?.subApps?.find((s) => s.name === 'feedbacks')}
                                                        parent={apps.find((a) => a.name === 'edp')}
                                                    />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/edp/objectives'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <ObjectivesSubAppWithData
                                                        app={apps.find((a) => a.name === 'edp')?.subApps?.find((s) => s.name === 'objectives')}
                                                        parent={apps.find((a) => a.name === 'edp')}
                                                    />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/edp/follow-ups'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                        <FollowUpSubAppWithData
                                                            app={apps
                                                                .find((a) => a.name === 'edp')
                                                                ?.subApps?.find((s) => s.name === 'follow-ups')}
                                                            parent={apps.find((a) => a.name === 'edp')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/edp/career'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <CareerSubAppWithData
                                                        app={apps.find((a) => a.name === 'edp')?.subApps?.find((s) => s.name === 'career')}
                                                        parent={apps.find((a) => a.name === 'edp')}
                                                    />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/edp/overallAssessment'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <OverallAssessmentSubAppWithData
                                                        app={apps
                                                            .find((a) => a.name === 'edp')
                                                                ?.subApps?.find((s) => s.name === 'overallAssessment')}
                                                            parent={apps.find((a) => a.name === 'edp')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/edp/team'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <MyTeamSubAppWithData
                                                        app={apps.find((a) => a.name === 'edp')?.subApps?.find((s) => s.name === 'team')}
                                                        parent={apps.find((a) => a.name === 'edp')}
                                                    />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/reference-years'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <ReferenceYearSubApp
                                                        app={apps.find((a) => a.name === 'synergy')?.subApps?.find((s) => s.name === 'year')}
                                                        parent={apps.find((a) => a.name === 'synergy')}
                                                    />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/business-categories'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <BusinessCategorySubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'busines-categories')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/regions'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <RegionSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'regions')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/offices'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <OfficeSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'offices')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/departments'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <DepartmentSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.url.endsWith('/departments'))}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/group-sizes'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <GroupSizeSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'group-size')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/group-types'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <GroupTypeSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'group-type')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/groups'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <GroupSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'groups')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/manager-type-group-sizes'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <ManagerTypeGroupSizeSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'type-size')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/manager-types'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <ManagerTypeSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'manager-types')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/managers'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <ManagerSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'managers')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/base-hr-level-families'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <BaseHrLevelFamilySubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'hrlevel')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/objectives'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <ObjectiveSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'objective-synergy')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/employee-exceptions'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <EmployeeSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'exception')}
                                                        parent={apps.find((a) => a.name === 'synergy')}
                                                    />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/synergy/reports'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <ReportSubApp
                                                        app={apps
                                                            .find((a) => a.name === 'synergy')
                                                            ?.subApps?.find((s) => s.name === 'reports')}
                                                            parent={apps.find((a) => a.name === 'synergy')}
                                                        />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/hierarchy'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <HierarchyApp 
                                                        app={apps
                                                            .find((a) => a.name === 'hierarchy')}
                                                    />
                                                </React.Suspense>}
                                            />
                                            <Route
                                                path='/documents-mfiles/modify'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <DocumentMFileModifyAppWithData
                                                        app={apps
                                                            .find((a) => a.name === 'mfiles-documents')
                                                            ?.subApps?.find((s) => s.name === 'ajustement')}
                                                        parent={apps.find((a) => a.name === 'mfiles-documents')}
                                                    />
                                                    </React.Suspense>}
                                            />
                                            <Route
                                                path='/documents-mfiles/configurations'
                                                render={() => <React.Suspense fallback={<LazyLoading />}>
                                                    <DocumentMFileConfigurationAppWithData
                                                        app={apps
                                                            .find((a) => a.name === 'mfiles-documents')
                                                            ?.subApps?.find((s) => s.name === 'configuration')}
                                                        parent={apps.find((a) => a.name === 'mfiles-documents')}
                                                    />
                                                    </React.Suspense>}
                                            />
                                            <Route path='/account' render={() => <Account />} />
                                            <Route
                                                exact
                                                path='/'
                                                render={() => <ApplicationsList apps={apps.filter((a) => a.name !== AppsEnum.home)} />}
                                            />
                                        </Switch>
                                    </>
                                )}
                                </MainContainer>
                        </GlobalContextProvider>
                    </DbContext.Provider>
                </LocalizationContext.Provider>
            </MuiThemeProvider>
        </>
    );
};

const HomeWithAccount = () => (
    <AccountProvider>
        <Home />
    </AccountProvider>
);

export default HomeWithAccount;
